import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../axiosInstance";
import SalesGrowthPieChart from "./SalesGrowthPieChart";
import DistributionPieCharts from "./DistributionPieCharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import logo from "../images/tvpLogo.png";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Download,
  TrendingUp,
  Package,
  Award,
  Target,
  MapPin,
  DollarSign,
  BarChart3,
  Smartphone,
  CreditCard,
  ShoppingBag,
  Eye,
  EyeOff,
  Truck,
  PieChart,
  Filter
} from "lucide-react";

const getMonthName = (monthIndex) => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return monthNames[monthIndex];
};

function ReportSettings({ reportType }) {
  const [currentReportType, setCurrentReportType] = useState(reportType || "today");
  const [reportData, setReportData] = useState(null);
  const [showPostalCodesTable, setShowPostalCodesTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedWeekDate, setSelectedWeekDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedYearMonth, setSelectedYearMonth] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [showItemsTable, setShowItemsTable] = useState(false);
  const [filterItemType, setFilterItemType] = useState("All");
  const [uniqueItemTypes, setUniqueItemTypes] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedEndDate, setSelectedEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [showSourceBreakdown, setShowSourceBreakdown] = useState(false);

  // New state for filtering
  const [sourceFilter, setSourceFilter] = useState("overall"); // overall, pos, website
  const [showOrderStats, setShowOrderStats] = useState(false);

  const pdfRef = useRef();
  const navigate = useNavigate();

  const goBack = () => navigate("/admin/reports");

  const reportTypes = [
    { id: "today", label: "Today's Report" },
    { id: "daily", label: "Daily Report" },
    { id: "range", label: "Date Range Report" },
    { id: "weekly", label: "Weekly Report" },
    { id: "monthly", label: "Monthly Report" }
  ];

  const generatePDF = async () => {
    const input = pdfRef.current;
    input.style.display = "block";
    await new Promise((res) => setTimeout(res, 500));
    const canvas = await html2canvas(input, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;
    while (heightLeft > 20) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }
    pdf.save("Sales_Report.pdf");
    input.style.display = "none";
  };

  const fetchReport = async (reportType = currentReportType) => {
    setIsLoading(true);
    setReportData(null);
    setFilterItemType("All");

    let url = `/admin/sales-report/unified?`;

    try {
      if (reportType === "today") {
        url += "period_type=today";
      } else if (reportType === "daily") {
        if (!selectedDate) {
          alert("Please select a date for the daily report.");
          setIsLoading(false);
          return;
        }
        url += `period_type=daily&date=${selectedDate}`;
      } else if (reportType === "weekly") {
        if (!selectedWeekDate) {
          alert("Please select a date for the weekly report.");
          setIsLoading(false);
          return;
        }
        url += `period_type=weekly&date=${selectedWeekDate}`;
      } else if (reportType === "monthly") {
        if (!selectedYearMonth || !selectedMonth) {
          alert("Please select a year and month for the monthly report.");
          setIsLoading(false);
          return;
        }
        url += `period_type=monthly&year=${selectedYearMonth}&month=${selectedMonth}`;
      } else if (reportType === "range") {
        if (!selectedStartDate || !selectedEndDate) {
          alert("Please select both start and end dates for the date range report.");
          setIsLoading(false);
          return;
        }
        url += `period_type=range&start_date=${selectedStartDate}&end_date=${selectedEndDate}`;
      }

      const res = await axiosInstance.get(url);
      setReportData(res.data);
      console.log("RESPONSE", res.data);

      // Extract unique item types
      if (res.data.analytics?.overall?.all_items_sold) {
        const uniqueTypes = ['All', ...new Set(
          res.data.analytics.overall.all_items_sold
            .map(item => item.type)
            .filter(Boolean)
        )];
        setUniqueItemTypes(uniqueTypes);
      }
    } catch (err) {
      console.error(`Error fetching ${reportType} report:`, err);
      alert(`Failed to fetch ${reportType} report. Please check your selections.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportTypeChange = (type) => {
    setCurrentReportType(type);
    setReportData(null);
    setSourceFilter("overall");
    fetchReport(type);
  };

  useEffect(() => {
    if (currentReportType === "today") {
      fetchReport();
    }
  }, [currentReportType]);

  useEffect(() => {
    if (currentReportType !== "today") {
      fetchReport();
    }
  }, [selectedDate, selectedWeekDate, selectedYearMonth, selectedMonth, selectedStartDate, selectedEndDate]);

  // Filter orders based on source filter
  const getFilteredOrders = () => {
    if (!reportData?.orders) return [];

    if (sourceFilter === "overall") {
      return reportData.orders;
    } else if (sourceFilter === "pos") {
      return reportData.orders.filter(order =>
        (order.order_source || "").toLowerCase() === "epos"
      );
    } else if (sourceFilter === "website") {
      return reportData.orders.filter(order =>
        (order.order_source || "").toLowerCase() === "website"
      );
    }
    return reportData.orders;
  };

  // Calculate statistics from filtered orders
  const calculateStats = () => {
    const filteredOrders = getFilteredOrders();

    const totalOrders = filteredOrders.length;

    const cancelledOrders = filteredOrders.filter(order =>
      order.status === 'red' || order.status === 'cancelled' || order.status === 'refunded'
    );
    const cancelledCount = cancelledOrders.length;
    const cancelledAmount = cancelledOrders.reduce((sum, order) =>
      sum + parseFloat(order.total_price || 0), 0
    );

    const completedOrders = filteredOrders.filter(order => order.status === 'blue').length;
    const pendingOrders = filteredOrders.filter(order => order.status === 'yellow' || order.status === "green").length;

    // Active orders (non-cancelled)
    const activeOrders = filteredOrders.filter(order =>
      order.status !== 'red' && order.status !== 'cancelled' && order.status !== 'refunded'
    );

    const totalSales = activeOrders.reduce((sum, order) =>
      sum + parseFloat(order.total_price || 0), 0
    );

    const totalDiscount = activeOrders.reduce((sum, order) =>
      sum + parseFloat(order.discount || 0), 0
    );

    // Total Sales = total_sales + total_discount + cancelledAmount
    const totalSalesWithDiscountAndCancelled = totalSales + totalDiscount + cancelledAmount;

    // Calculate cash sales (including discount, excluding paidouts, subtracting cancelled cash orders)
    const cashOrders = activeOrders.filter(order =>
      (order.payment_type || "").toLowerCase().includes('cash')
    );
    const cashSales = cashOrders.reduce((sum, order) =>
      sum + parseFloat(order.total_price || 0), 0
    );
    const cashDiscount = cashOrders.reduce((sum, order) =>
      sum + parseFloat(order.discount || 0), 0
    );
    const cancelledCashOrders = cancelledOrders.filter(order =>
      (order.payment_type || "").toLowerCase().includes('cash')
    );
    const cancelledCashAmount = cancelledCashOrders.reduce((sum, order) =>
      sum + parseFloat(order.total_price || 0), 0
    );
    const paidoutsTotal = sourceFilter === "overall"
      ? (reportData?.paidouts?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0)
      : 0;
    let totalCashAmount = cashSales;

    // Calculate POS card sales (card payment + pos source)
    const posCardOrders = activeOrders.filter(order =>
      (order.payment_type || "").toLowerCase() === 'card' &&
      (order.order_source || "").toLowerCase() === 'epos'
    );
    const posCardSales = posCardOrders.reduce((sum, order) =>
      sum + parseFloat(order.total_price || 0), 0
    );
    const posCardDiscount = posCardOrders.reduce((sum, order) =>
      sum + parseFloat(order.discount || 0), 0
    );
    const cancelledPOSCardOrders = cancelledOrders.filter(order =>
      (order.payment_type || "").toLowerCase() === 'card' &&
      (order.order_source || "").toLowerCase() === 'epos'
    );
    const cancelledPOSCardAmount = cancelledPOSCardOrders.reduce((sum, order) =>
      sum + parseFloat(order.total_price || 0), 0
    );
    const totalPOSCardAmount = posCardSales - posCardDiscount - cancelledPOSCardAmount;

    // Calculate Stripe card sales (Card or card_through_link)
    const stripeCardOrders = activeOrders.filter(order => {
      const paymentType = (order.payment_type || "").toLowerCase();
      return paymentType === 'card' && (order.order_source || "").toLowerCase() === 'website' ||
        paymentType === 'card_through_link';
    });
    const stripeCardSales = stripeCardOrders.reduce((sum, order) =>
      sum + parseFloat(order.total_price || 0), 0
    );
    const stripeCardDiscount = stripeCardOrders.reduce((sum, order) =>
      sum + parseFloat(order.discount || 0), 0
    );
    const totalStripeAmount = stripeCardSales;

    // Calculate unpaid orders
    const unpaidOrders = activeOrders.filter(order =>
      !order.payment_type || order.payment_type == ""
    );
    const unpaidAmount = unpaidOrders.reduce((sum, order) =>
      sum + parseFloat(order.total_price || 0), 0
    );

    // Net sales
    const netSales = totalSalesWithDiscountAndCancelled - totalDiscount - paidoutsTotal - cancelledAmount;

    // Calculate sales growth
    const comparisonSales = reportData?.comparison_period_sales || 0;
    const salesIncrease = totalSales - comparisonSales;
    const salesGrowthPercentage = comparisonSales > 0
      ? ((totalSales - comparisonSales) / comparisonSales) * 100
      : 0;

    return {
      totalOrders,
      cancelledCount,
      cancelledAmount,
      completedOrders,
      pendingOrders,
      totalSales,
      totalSalesWithDiscountAndCancelled,
      totalDiscount,
      totalCashAmount,
      totalPOSCardAmount,
      totalStripeAmount,
      unpaidAmount,
      paidoutsTotal,
      netSales,
      salesIncrease,
      salesGrowthPercentage
    };
  };

  // Get aggregated data for charts
  const getAggregatedChartData = () => {
    const filteredOrders = getFilteredOrders();

    // Aggregate by payment type
    const paymentMap = {};
    filteredOrders.forEach(order => {
      if (order.status === 'red' || order.status === 'cancelled' || order.status === 'refunded') return;

      let paymentType = order.payment_type || "Unpaid";
      if (paymentType === "") paymentType = "Unpaid";

      if (!paymentMap[paymentType]) {
        paymentMap[paymentType] = { payment_type: paymentType, count: 0, total: 0 };
      }
      paymentMap[paymentType].count += 1;
      paymentMap[paymentType].total += parseFloat(order.total_price || 0);
    });

    // Aggregate by order type
    const orderTypeMap = {};
    filteredOrders.forEach(order => {
      if (order.status === 'red' || order.status === 'cancelled' || order.status === 'refunded') return;

      const orderType = order.order_type || "Unknown";
      if (!orderTypeMap[orderType]) {
        orderTypeMap[orderType] = { order_type: orderType, count: 0, total: 0 };
      }
      orderTypeMap[orderType].count += 1;
      orderTypeMap[orderType].total += parseFloat(order.total_price || 0);
    });

    // Aggregate by order source
    const orderSourceMap = {};
    filteredOrders.forEach(order => {
      if (order.status === 'red' || order.status === 'cancelled' || order.status === 'refunded') return;

      const source = order.order_source || "Unknown";
      if (!orderSourceMap[source]) {
        orderSourceMap[source] = { source: source, count: 0, total: 0 };
      }
      orderSourceMap[source].count += 1;
      orderSourceMap[source].total += parseFloat(order.total_price || 0);
    });

    return {
      sales_by_payment_type: Object.values(paymentMap),
      sales_by_order_type: Object.values(orderTypeMap),
      sales_by_order_source: Object.values(orderSourceMap)
    };
  };

  const stats = reportData ? calculateStats() : null;
  const chartData = reportData ? getAggregatedChartData() : null;

  // Calculate sales breakdown for all sources
  const getSalesBreakdown = () => {
    if (!reportData?.orders) return null;

    const allOrders = reportData.orders.filter(order =>
      order.status !== 'red' && order.status !== 'cancelled' && order.status !== 'refunded'
    );

    const totalSales = allOrders.reduce((sum, order) =>
      sum + parseFloat(order.total_price || 0), 0
    );

    const posOrders = allOrders.filter(order =>
      (order.order_source || "").toLowerCase() === "epos"
    );
    const posSales = posOrders.reduce((sum, order) =>
      sum + parseFloat(order.total_price || 0), 0
    );

    const websiteOrders = allOrders.filter(order =>
      (order.order_source || "").toLowerCase() === "website"
    );
    const websiteSales = websiteOrders.reduce((sum, order) =>
      sum + parseFloat(order.total_price || 0), 0
    );

    return {
      totalSales,
      posSales,
      websiteSales
    };
  };

  const salesBreakdown = reportData ? getSalesBreakdown() : null;

  // Get analytics based on source filter
  const getFilteredAnalytics = () => {
    if (!reportData?.analytics) return null;

    if (sourceFilter === "overall") {
      return reportData.analytics.overall;
    } else if (sourceFilter === "pos") {
      return reportData.analytics.pos;
    } else if (sourceFilter === "website") {
      return reportData.analytics.website;
    }
    return reportData.analytics.overall;
  };

  const analytics = getFilteredAnalytics();

  const hasData = (data) => data && Array.isArray(data) && data.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 px-4 py-2">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <button
            onClick={goBack}
            className="group flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-all mb-4 md:mb-6 font-medium text-sm md:text-base"
            style={{ fontFamily: "Bambino" }}
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Reports</span>
          </button>

          <div className="flex items-center justify-between gap-2">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-blue-500 bg-clip-text text-transparent" style={{ fontFamily: "Bambino", lineHeight: "1.2" }}>
              Sales Report
            </h1>
            <BarChart3 className="w-8 h-8 md:w-12 md:h-12 text-purple-500" />
          </div>
        </div>

        {/* Report Type Switcher */}
        <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-3 md:p-4 shadow-xl mb-6 md:mb-8 border-2 border-purple-100">
          <div className="flex flex-wrap gap-2 md:gap-3">
            {reportTypes.map((report) => (
              <button
                key={report.id}
                onClick={() => handleReportTypeChange(report.id)}
                disabled={isLoading}
                className={`px-4 md:px-6 py-2.5 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all text-sm md:text-base ${currentReportType === report.id
                  ? 'bg-gradient-to-r from-purple-400 to-pink-400 text-white shadow-lg'
                  : 'bg-white text-gray-700 border-2 border-purple-200 hover:border-purple-400'
                  } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ fontFamily: "Bambino" }}
              >
                {report.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date Selection Card */}
        <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-xl mb-6 md:mb-8 border-2 border-purple-100">
          {/* Date Selection Row */}
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3 md:gap-4 pb-4 md:pb-6 border-b border-purple-200">
            {currentReportType === "daily" && (
              <>
                <label className="font-semibold text-gray-700 text-sm md:text-base" style={{ fontFamily: "Bambino" }}>Select Date:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  disabled={isLoading}
                  className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "Bambino" }}
                />
              </>
            )}
            {currentReportType === "range" && (
              <>
                <label className="font-semibold text-gray-700 text-sm md:text-base" style={{ fontFamily: "Bambino" }}>Start Date:</label>
                <input
                  type="date"
                  value={selectedStartDate}
                  onChange={(e) => setSelectedStartDate(e.target.value)}
                  disabled={isLoading}
                  className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "Bambino" }}
                />
                <label className="font-semibold text-gray-700 text-sm md:text-base" style={{ fontFamily: "Bambino" }}>End Date:</label>
                <input
                  type="date"
                  value={selectedEndDate}
                  onChange={(e) => setSelectedEndDate(e.target.value)}
                  disabled={isLoading}
                  className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "Bambino" }}
                />
              </>
            )}
            {currentReportType === "weekly" && (
              <>
                <label className="font-semibold text-gray-700 text-sm md:text-base" style={{ fontFamily: "Bambino" }}>Select Date:</label>
                <input
                  type="date"
                  value={selectedWeekDate}
                  onChange={(e) => setSelectedWeekDate(e.target.value)}
                  disabled={isLoading}
                  className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "Bambino" }}
                />
              </>
            )}
            {currentReportType === "monthly" && (
              <>
                <label className="font-semibold text-gray-700 text-sm md:text-base" style={{ fontFamily: "Bambino" }}>Year:</label>
                <input
                  type="number"
                  value={selectedYearMonth}
                  onChange={(e) => setSelectedYearMonth(parseInt(e.target.value))}
                  disabled={isLoading}
                  className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 w-24 md:w-28 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "Bambino" }}
                />
                <label className="font-semibold text-gray-700 text-sm md:text-base" style={{ fontFamily: "Bambino" }}>Month:</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  disabled={isLoading}
                  className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "Bambino" }}
                >
                  {[...Array(12).keys()].map(i => (
                    <option key={i} value={i + 1}>{getMonthName(i)}</option>
                  ))}
                </select>
              </>
            )}
            {currentReportType !== "today" && (
              <button
                onClick={generatePDF}
                disabled={isLoading}
                className="bg-gradient-to-r from-blue-400 to-cyan-400 text-white px-6 md:px-8 py-2.5 md:py-3 rounded-lg md:rounded-xl hover:from-blue-500 hover:to-cyan-500 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold text-sm md:text-base w-full sm:w-auto flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                style={{ fontFamily: "Bambino" }}
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
            )}
          </div>

          {/* Source Filter Buttons */}
          {reportData && (
            <div className="mt-4 md:mt-6 flex flex-col gap-4">
              {/* Sales Breakdown Cards */}
              {salesBreakdown && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 pb-4 border-b border-purple-200">
                  {/* Total Sales */}
                  <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-purple-300">
                    <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                      <DollarSign className="w-4 h-4" />
                      Total Sales
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-purple-700" style={{ fontFamily: "Bambino" }}>
                      £{salesBreakdown.totalSales.toFixed(2)}
                    </div>
                  </div>

                  {/* POS Sales */}
                  <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-blue-300">
                    <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                      <Smartphone className="w-4 h-4" />
                      POS Sales
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-blue-700" style={{ fontFamily: "Bambino" }}>
                      £{salesBreakdown.posSales.toFixed(2)}
                    </div>
                  </div>

                  {/* Website Sales */}
                  <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-green-300">
                    <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                      <ShoppingBag className="w-4 h-4" />
                      Website Sales
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-green-700" style={{ fontFamily: "Bambino" }}>
                      £{salesBreakdown.websiteSales.toFixed(2)}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 md:gap-3">
                <button
                  onClick={() => setSourceFilter("overall")}
                  className={`px-4 md:px-6 py-2 md:py-2.5 rounded-lg md:rounded-xl font-semibold transition-all text-sm md:text-base ${sourceFilter === "overall"
                    ? 'bg-gradient-to-r from-purple-400 to-pink-400 text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-purple-200 hover:border-purple-400'
                    }`}
                  style={{ fontFamily: "Bambino" }}
                >
                  Overall
                </button>
                <button
                  onClick={() => setSourceFilter("pos")}
                  className={`px-4 md:px-6 py-2 md:py-2.5 rounded-lg md:rounded-xl font-semibold transition-all text-sm md:text-base ${sourceFilter === "pos"
                    ? 'bg-gradient-to-r from-blue-400 to-cyan-400 text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-blue-200 hover:border-blue-400'
                    }`}
                  style={{ fontFamily: "Bambino" }}
                >
                  POS
                </button>
                <button
                  onClick={() => setSourceFilter("website")}
                  className={`px-4 md:px-6 py-2 md:py-2.5 rounded-lg md:rounded-xl font-semibold transition-all text-sm md:text-base ${sourceFilter === "website"
                    ? 'bg-gradient-to-r from-green-400 to-emerald-400 text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-green-200 hover:border-green-400'
                    }`}
                  style={{ fontFamily: "Bambino" }}
                >
                  Website
                </button>
                <button
                  onClick={() => setShowOrderStats(!showOrderStats)}
                  className="ml-auto px-4 md:px-6 py-2 md:py-2.5 bg-gradient-to-r from-orange-400 to-red-400 text-white rounded-lg md:rounded-xl hover:from-orange-500 hover:to-red-500 transition-all shadow-lg hover:shadow-xl font-semibold flex items-center gap-2 text-sm md:text-base"
                  style={{ fontFamily: "Bambino" }}
                >
                  {showOrderStats ? (
                    <>
                      <EyeOff className="w-4 h-4" />
                      Hide Stats
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      Show Stats
                    </>
                  )}
                </button>
              </div>

              {/* Order Statistics */}
              {showOrderStats && stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-4">
                  {/* Total Orders */}
                  <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-blue-300">
                    <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold" style={{ fontFamily: "Bambino" }}>
                      Total Orders
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-blue-700" style={{ fontFamily: "Bambino" }}>
                      {stats.totalOrders}
                    </div>
                  </div>

                  {/* Cancelled Orders */}
                  <div className="bg-gradient-to-br from-red-100 to-pink-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-red-300">
                    <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold" style={{ fontFamily: "Bambino" }}>
                      Cancelled Orders
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-red-700" style={{ fontFamily: "Bambino" }}>
                      {stats.cancelledCount}
                    </div>
                    <div className="text-xs text-gray-600 font-medium" style={{ fontFamily: "Bambino" }}>
                      £{stats.cancelledAmount.toFixed(2)}
                    </div>
                  </div>

                  {/* Completed Orders */}
                  <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-green-300">
                    <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold" style={{ fontFamily: "Bambino" }}>
                      Completed Orders
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-green-700" style={{ fontFamily: "Bambino" }}>
                      {stats.completedOrders}
                    </div>
                  </div>

                  {/* Pending Orders */}
                  <div className="bg-gradient-to-br from-yellow-100 to-amber-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-yellow-300">
                    <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold" style={{ fontFamily: "Bambino" }}>
                      Pending Orders
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-yellow-700" style={{ fontFamily: "Bambino" }}>
                      {stats.pendingOrders}
                    </div>
                  </div>

                  {/* Total Cash */}
                  <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-purple-300">
                    <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold" style={{ fontFamily: "Bambino" }}>
                      Total Cash
                    </div>
                    <div className="text-xl md:text-2xl font-bold text-purple-700" style={{ fontFamily: "Bambino" }}>
                      £{stats.totalCashAmount.toFixed(2)}
                    </div>
                  </div>

                  {/* Total POS Card */}
                  <div className="bg-gradient-to-br from-indigo-100 to-blue-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-indigo-300">
                    <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold" style={{ fontFamily: "Bambino" }}>
                      Total POS Card
                    </div>
                    <div className="text-xl md:text-2xl font-bold text-indigo-700" style={{ fontFamily: "Bambino" }}>
                      £{stats.totalPOSCardAmount.toFixed(2)}
                    </div>
                  </div>

                  {/* Total Stripe Card */}
                  <div className="bg-gradient-to-br from-teal-100 to-cyan-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-teal-300">
                    <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold" style={{ fontFamily: "Bambino" }}>
                      Total Stripe Card
                    </div>
                    <div className="text-xl md:text-2xl font-bold text-teal-700" style={{ fontFamily: "Bambino" }}>
                      £{stats.totalStripeAmount.toFixed(2)}
                    </div>
                  </div>

                  {/* Unpaid Orders */}
                  <div className="bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-orange-300">
                    <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold" style={{ fontFamily: "Bambino" }}>
                      Unpaid Orders
                    </div>
                    <div className="text-xl md:text-2xl font-bold text-orange-700" style={{ fontFamily: "Bambino" }}>
                      £{stats.unpaidAmount.toFixed(2)}
                    </div>
                  </div>

                  {/* Total Paidouts - Only show for Overall filter */}
                  {sourceFilter === 'overall' && (
                    <div className="bg-gradient-to-br from-orange-100 to-red-100 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-orange-300">
                      <div className="text-xs md:text-sm text-gray-600 mb-1 font-semibold flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                        <DollarSign className="w-4 h-4" />
                        Total Paidouts
                      </div>
                      <div className="text-xl md:text-2xl font-bold text-orange-700" style={{ fontFamily: "Bambino" }}>
                        £{stats.paidoutsTotal.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center gap-4 py-16">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-purple-200 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-t-purple-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
            </div>
            <span className="text-lg font-semibold text-gray-700" style={{ fontFamily: "Bambino" }}>
              Loading your report...
            </span>
          </div>
        )}

        {/* Report Data Display */}
        {reportData && stats && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Summary Card */}
              <div className="lg:col-span-1">
                <div className="bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-xl border-2 border-purple-200 lg:sticky lg:top-6">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6 flex items-center gap-2" style={{ fontFamily: "Bambino" }}>
                    <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-purple-600" />
                    Summary
                  </h3>

                  <div className="space-y-3 md:space-y-4">
                    {/* Period */}
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 border border-purple-200">
                      <div className="text-xs md:text-sm text-gray-600 mb-1 font-medium flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                        <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                        Period
                      </div>
                      <div className="text-sm md:text-base font-bold text-gray-800 break-words" style={{ fontFamily: "Bambino" }}>
                        {reportData.period.from === reportData.period.to
                          ? new Date(reportData.period.from).toLocaleDateString("en-GB").replace(/\//g, "-")
                          : `${new Date(reportData.period.from).toLocaleDateString("en-GB").replace(/\//g, "-")} → ${new Date(reportData.period.to).toLocaleDateString("en-GB").replace(/\//g, "-")}`
                        }
                      </div>
                    </div>

                    {/* Total Orders */}
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 border border-blue-200">
                      <div className="text-xs md:text-sm text-gray-600 mb-1 font-medium flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                        <Package className="w-3 h-3 md:w-4 md:h-4" />
                        Total Orders
                      </div>
                      <div className="text-xl md:text-2xl font-bold text-blue-600" style={{ fontFamily: "Bambino" }}>{stats.totalOrders}</div>
                    </div>

                    {/* Sales Growth */}
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 border border-green-200">
                      <div className="text-xs md:text-sm text-gray-600 mb-1 font-medium flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                        <BarChart3 className="w-3 h-3 md:w-4 md:h-4" />
                        Sales Growth
                      </div>
                      <div className={`text-xl md:text-2xl font-bold ${stats.salesGrowthPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`} style={{ fontFamily: "Bambino" }}>
                        {stats.salesGrowthPercentage > 0 ? '+' : ''}{stats.salesGrowthPercentage.toFixed(2)}%
                      </div>
                      <div className={`text-lg md:text-xl font-bold ${stats.salesIncrease >= 0 ? 'text-green-600' : 'text-red-600'}`} style={{ fontFamily: "Bambino" }}>
                        £{stats.salesIncrease > 0 ? '+' : ''}{stats.salesIncrease.toFixed(2)}
                      </div>
                    </div>

                    {/* Most Sold Item */}
                    {analytics?.most_sold_item?.item_name && (
                      <div className="bg-white/60 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 border border-yellow-200">
                        <div className="text-xs md:text-sm text-gray-600 mb-1 font-medium flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                          <Award className="w-3 h-3 md:w-4 md:h-4" />
                          Most Sold Item
                        </div>
                        <div className="text-sm md:text-base font-bold text-gray-800 break-words" style={{ fontFamily: "Bambino" }}>
                          {analytics.most_sold_item.item_name}
                        </div>
                        <div className="text-xs md:text-sm text-gray-600 mt-1" style={{ fontFamily: "Bambino" }}>
                          {analytics.most_sold_item.quantity_sold} sold
                        </div>
                        {analytics.most_sold_item.percentage_increase !== undefined && (
                          <div className={`text-xs md:text-sm font-semibold mt-1 ${analytics.most_sold_item.percentage_increase >= 0 ? 'text-green-600' : 'text-red-600'}`} style={{ fontFamily: "Bambino" }}>
                            {analytics.most_sold_item.percentage_increase >= 0 ? '+' : ''}{analytics.most_sold_item.percentage_increase}% vs last period
                          </div>
                        )}
                      </div>
                    )}

                    {/* Most Sold Category */}
                    {analytics?.most_sold_type?.type && (
                      <div className="bg-white/60 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 border border-pink-200">
                        <div className="text-xs md:text-sm text-gray-600 mb-1 font-medium flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                          <Target className="w-3 h-3 md:w-4 md:h-4" />
                          Most Sold Category
                        </div>
                        <div className="text-sm md:text-base font-bold text-gray-800" style={{ fontFamily: "Bambino" }}>
                          {analytics.most_sold_type.type}
                        </div>
                        <div className="text-xs md:text-sm text-gray-600 mt-1" style={{ fontFamily: "Bambino" }}>
                          {analytics.most_sold_type.quantity_sold} sold
                        </div>
                        {analytics.most_sold_type.percentage_increase !== undefined && (
                          <div className={`text-xs md:text-sm font-semibold mt-1 ${analytics.most_sold_type.percentage_increase >= 0 ? 'text-green-600' : 'text-red-600'}`} style={{ fontFamily: "Bambino" }}>
                            {analytics.most_sold_type.percentage_increase >= 0 ? '+' : ''}{analytics.most_sold_type.percentage_increase}% vs last period
                          </div>
                        )}
                      </div>
                    )}

                    {/* Most Delivered Area */}
                    {analytics?.most_delivered_postal_code && (
                      <div className="bg-white/60 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 border border-cyan-200">
                        <div className="text-xs md:text-sm text-gray-600 mb-1 font-medium flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                          <MapPin className="w-3 h-3 md:w-4 md:h-4" />
                          Most Delivered Area
                        </div>
                        <div className="text-sm md:text-base font-bold text-gray-800" style={{ fontFamily: "Bambino" }}>
                          {analytics.most_delivered_postal_code.postal_code}
                        </div>
                        <div className="text-xs md:text-sm text-gray-600 mt-1" style={{ fontFamily: "Bambino" }}>
                          {analytics.most_delivered_postal_code.delivery_count} deliveries
                        </div>
                      </div>
                    )}

                    {/* Sales Details */}
                    <div className="bg-gradient-to-br from-purple-200 to-pink-200 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-purple-300">
                      <div className="text-sm md:text-base font-bold text-gray-800 mb-2 md:mb-3 flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                        <span>£</span>
                        Sales Details
                      </div>

                      {/* Total Sales (including discount and cancelled) */}
                      <div className="flex justify-between items-center mb-2 gap-2">
                        <span className="text-xs md:text-sm font-semibold text-gray-700" style={{ fontFamily: "Bambino" }}>Total Sales:</span>
                        <span className="text-base md:text-lg font-bold text-purple-700 whitespace-nowrap" style={{ fontFamily: "Bambino" }}>
                          £{stats.totalSalesWithDiscountAndCancelled.toFixed(2)}
                        </span>
                      </div>

                      {/* Total Discount */}
                      <div className="flex justify-between items-center mb-2 gap-2">
                        <span className="text-xs md:text-sm font-semibold text-gray-700" style={{ fontFamily: "Bambino" }}>Total Discount:</span>
                        <span className="text-sm md:text-base font-bold text-pink-600 whitespace-nowrap" style={{ fontFamily: "Bambino" }}>
                          £{stats.totalDiscount.toFixed(2)}
                        </span>
                      </div>

                      {/* Total Paidouts */}
                      {sourceFilter === 'overall' && (
                        <div className="flex justify-between items-center mb-2 gap-2">
                          <span className="text-xs md:text-sm font-semibold text-gray-700" style={{ fontFamily: "Bambino" }}>Total Paidouts:</span>
                          <span className="text-sm md:text-base font-bold text-orange-600 whitespace-nowrap" style={{ fontFamily: "Bambino" }}>
                            £{stats.paidoutsTotal.toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Cancelled Orders */}
                      <div className="flex justify-between items-center mb-2 gap-2">
                        <span className="text-xs md:text-sm font-semibold text-gray-700" style={{ fontFamily: "Bambino" }}>Cancelled Orders:</span>
                        <span className="text-sm md:text-base font-bold text-red-600 whitespace-nowrap" style={{ fontFamily: "Bambino" }}>
                          £{stats.cancelledAmount.toFixed(2)}
                        </span>
                      </div>

                      {/* Net Sales */}
                      <div className="flex justify-between items-center pt-2 mt-2 border-t-2 border-purple-300 gap-2">
                        <span className="text-xs md:text-sm font-bold text-gray-800" style={{ fontFamily: "Bambino" }}>
                          Net Sales:
                        </span>
                        <span className="text-lg md:text-xl font-bold text-purple-800 whitespace-nowrap" style={{ fontFamily: "Bambino" }}>
                          £{stats.netSales.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Section */}
              <div className="lg:col-span-2">
                <div className="bg-white/70 backdrop-blur-md rounded-3xl p-4 md:p-6 shadow-xl border-2 border-blue-100">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6 flex items-center gap-2" style={{ fontFamily: "Bambino" }}>
                    <PieChart className="w-6 h-6 md:w-8 md:h-8 text-blue-500" />
                    Analytics Overview
                  </h3>

                  {/* Main Charts */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
                    <div className="w-full">
                      <SalesGrowthPieChart growthPercentage={stats.salesIncrease} period={currentReportType} />
                    </div>
                    {hasData(chartData.sales_by_payment_type) && (
                      <div className="w-full">
                        <DistributionPieCharts data={chartData.sales_by_payment_type} title="Payment Methods" />
                      </div>
                    )}
                    {hasData(chartData.sales_by_order_type) && (
                      <div className="w-full">
                        <DistributionPieCharts data={chartData.sales_by_order_type} title="Order Types" />
                      </div>
                    )}
                    {hasData(chartData.sales_by_order_source) && (
                      <div className="w-full">
                        <DistributionPieCharts data={chartData.sales_by_order_source} title="Order Sources" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Deliveries by Postal Code */}
            {hasData(reportData.deliveries_by_postal_code) && (
              <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-xl border-2 border-green-100">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-6 gap-3">
                  <h3 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2 flex-wrap" style={{ fontFamily: "Bambino" }}>
                    <MapPin className="w-6 h-6 md:w-8 md:h-8 text-green-500" />
                    <span>Deliveries by Postal Code</span>
                    <span className="text-sm md:text-lg font-normal text-gray-600">({reportData.deliveries_by_postal_code.length} locations)</span>
                  </h3>
                  <button
                    onClick={() => setShowPostalCodesTable(!showPostalCodesTable)}
                    className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-green-400 to-cyan-400 text-white rounded-lg md:rounded-xl hover:from-green-500 hover:to-cyan-500 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold flex items-center gap-2 text-sm md:text-base w-full sm:w-auto justify-center"
                    style={{ fontFamily: "Bambino" }}
                  >
                    {showPostalCodesTable ? (
                      <>
                        <EyeOff className="w-4 h-4" />
                        Hide
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" />
                        Show
                      </>
                    )}
                  </button>
                </div>
                {showPostalCodesTable && (
                  <div className="overflow-x-auto rounded-lg md:rounded-xl -mx-4 md:mx-0">
                    <div className="inline-block min-w-full align-middle px-4 md:px-0">
                      <table className="w-full border-collapse min-w-[500px]">
                        <thead>
                          <tr className="bg-gradient-to-r from-green-100 to-cyan-100">
                            <th className="border-2 border-green-200 p-3 md:p-4 text-left font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Postal Code</th>
                            <th className="border-2 border-green-200 p-3 md:p-4 text-center font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Deliveries</th>
                            <th className="border-2 border-green-200 p-3 md:p-4 text-right font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Total Sales</th>
                          </tr>
                        </thead>
                        <tbody style={{ fontFamily: "Bambino" }}>
                          {reportData.deliveries_by_postal_code.map((location, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white/50' : 'bg-green-50/30'}>
                              <td className="border border-green-100 p-3 md:p-4 font-semibold text-xs md:text-base">{location.postal_code}</td>
                              <td className="border border-green-100 p-3 md:p-4 text-center font-bold text-blue-600 text-xs md:text-base">{location.delivery_count}</td>
                              <td className="border border-green-100 p-3 md:p-4 text-right font-bold text-green-600 text-xs md:text-base">£{parseFloat(location.total_delivery_sales).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* All Items Sold */}
            {hasData(analytics?.all_items_sold) && (() => {
              const filteredItems = filterItemType === "All"
                ? analytics.all_items_sold
                : analytics.all_items_sold.filter(item => item.type === filterItemType);

              return (
                <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-xl border-2 border-yellow-100">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-6 gap-3">
                    <h3 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2 flex-wrap" style={{ fontFamily: "Bambino" }}>
                      <Package className="w-6 h-6 md:w-8 md:h-8 text-yellow-500" />
                      <span>All Items Sold</span>
                      <span className="text-sm md:text-lg font-normal text-gray-600">({filteredItems.length} items)</span>
                    </h3>
                    <button
                      onClick={() => setShowItemsTable(!showItemsTable)}
                      className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-lg md:rounded-xl hover:from-yellow-500 hover:to-orange-500 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold flex items-center gap-2 text-sm md:text-base w-full sm:w-auto justify-center"
                      style={{ fontFamily: "Bambino" }}
                    >
                      {showItemsTable ? (
                        <>
                          <EyeOff className="w-4 h-4" />
                          Hide
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4" />
                          Show
                        </>
                      )}
                    </button>
                  </div>

                  {showItemsTable && (
                    <>
                      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <label className="font-bold text-gray-700 text-sm md:text-base whitespace-nowrap flex items-center gap-2" style={{ fontFamily: "Bambino" }}>
                          <Target className="w-4 h-4" />
                          Filter by Type:
                        </label>
                        <select
                          value={filterItemType}
                          onChange={(e) => setFilterItemType(e.target.value)}
                          className="p-2.5 md:p-3 border-2 border-yellow-200 rounded-lg md:rounded-xl w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent transition-all bg-white font-medium text-sm md:text-base"
                          style={{ fontFamily: "Bambino" }}
                        >
                          {uniqueItemTypes.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>

                      <div className="overflow-x-auto rounded-lg md:rounded-xl -mx-4 md:mx-0">
                        <div className="inline-block min-w-full align-middle px-4 md:px-0">
                          <table className="w-full border-collapse min-w-[700px]">
                            <thead>
                              <tr className="bg-gradient-to-r from-yellow-100 to-orange-100">
                                <th className="border-2 border-yellow-200 p-3 md:p-4 text-left font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Item Name</th>
                                <th className="border-2 border-yellow-200 p-3 md:p-4 text-left font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Type</th>
                                <th className="border-2 border-yellow-200 p-3 md:p-4 text-center font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Qty</th>
                                <th className="border-2 border-yellow-200 p-3 md:p-4 text-right font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Sales</th>
                                <th className="border-2 border-yellow-200 p-3 md:p-4 text-center font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Orders</th>
                              </tr>
                            </thead>
                            <tbody style={{ fontFamily: "Bambino" }}>
                              {filteredItems.map((item, idx) => (
                                <tr key={idx} className={idx % 2 === 0 ? 'bg-white/50' : 'bg-yellow-50/30'}>
                                  <td className="border border-yellow-100 p-3 md:p-4 font-semibold text-xs md:text-base">{item.item_name}</td>
                                  <td className="border border-yellow-100 p-3 md:p-4 text-xs md:text-base">{item.type || 'N/A'}</td>
                                  <td className="border border-yellow-100 p-3 md:p-4 text-center font-bold text-blue-600 text-xs md:text-base">{item.total_quantity_sold}</td>
                                  <td className="border border-yellow-100 p-3 md:p-4 text-right font-bold text-green-600 text-xs md:text-base">£{parseFloat(item.total_item_sales).toFixed(2)}</td>
                                  <td className="border border-yellow-100 p-3 md:p-4 text-center text-xs md:text-base">{item.orders_containing_item}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* PDF Export Hidden Section */}
        
        <div
          ref={pdfRef}
          id="pdf-content"
          style={{
            width: "210mm",
            maxWidth: "210mm",
            minHeight: "auto",
            padding: "15mm",
            backgroundColor: "#fff",
            color: "#000",
            fontFamily: "Arial, sans-serif",
            display: "none",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", borderBottom: "2px solid #ddd", paddingBottom: "15px" }}>
            <h1 style={{ margin: 0, fontSize: "28px", fontWeight: "bold" }}>
              Sales Report - {currentReportType.charAt(0).toUpperCase() + currentReportType.slice(1)}
            </h1>
            <img style={{ height: "64px", width: "auto" }} src={logo} alt="Logo" />
          </div>

          {reportData && stats && (
            <>
              {/* 1. Period */}
              <div style={{ marginBottom: "20px", pageBreakInside: "avoid" }}>
                <h2 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "10px", color: "#6a4c93", borderBottom: "2px solid #6a4c93", paddingBottom: "5px" }}>Period</h2>
                <p style={{ fontSize: "14px", margin: 0, padding: "8px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                  {reportData.period.from === reportData.period.to
                    ? new Date(reportData.period.from).toLocaleDateString("en-GB").replace(/\//g, "-")
                    : `${new Date(reportData.period.from).toLocaleDateString("en-GB").replace(/\//g, "-")} to ${new Date(reportData.period.to).toLocaleDateString("en-GB").replace(/\//g, "-")}`
                  }
                </p>
              </div>

              {/* 2. Total Orders */}
              <div style={{ marginBottom: "20px", pageBreakInside: "avoid" }}>
                <h2 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "10px", color: "#6a4c93", borderBottom: "2px solid #6a4c93", paddingBottom: "5px" }}>Total Orders</h2>
                <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0, padding: "8px", backgroundColor: "#f5f5f5", borderRadius: "4px", color: "#2d5016" }}>
                  {stats.totalOrders}
                </p>
              </div>

              {/* 3. Sales Details */}
              <div style={{ marginBottom: "25px", pageBreakInside: "avoid" }}>
                <h2 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "12px", color: "#6a4c93", borderBottom: "2px solid #6a4c93", paddingBottom: "5px" }}>Sales Details</h2>

                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "15px" }}>
                  <tbody>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", width: "50%", fontSize: "14px" }}>Total Sales Amount</td>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", color: "#2d5016", fontSize: "16px" }}>
                        £{stats.totalSalesWithDiscountAndCancelled.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", fontSize: "14px" }}>Total Discount</td>
                      <td style={{ padding: "10px", border: "1px solid #ddd", color: "#c1121f", fontWeight: "bold", fontSize: "16px" }}>
                        £{stats.totalDiscount.toFixed(2)}
                      </td>
                    </tr>
                    {sourceFilter === 'overall' && (
                      <tr style={{ backgroundColor: "#f5f5f5" }}>
                        <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", fontSize: "14px" }}>Total Paidouts</td>
                        <td style={{ padding: "10px", border: "1px solid #ddd", color: "#ea6317", fontWeight: "bold", fontSize: "16px" }}>
                          £{stats.paidoutsTotal.toFixed(2)}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", fontSize: "14px" }}>Cancelled Orders</td>
                      <td style={{ padding: "10px", border: "1px solid #ddd", color: "#c1121f", fontWeight: "bold", fontSize: "16px" }}>
                        £{stats.cancelledAmount.toFixed(2)}
                      </td>
                    </tr>
                    <tr style={{ backgroundColor: "#fffbea" }}>
                      <td style={{ padding: "12px", border: "2px solid #6a4c93", fontWeight: "bold", fontSize: "16px" }}>Net Sales</td>
                      <td style={{ padding: "12px", border: "2px solid #6a4c93", color: "#6a4c93", fontWeight: "bold", fontSize: "18px" }}>
                        £{stats.netSales.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 4. Statistics */}
              <div style={{ marginBottom: "25px", pageBreakInside: "avoid" }}>
                <h2 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "12px", color: "#6a4c93", borderBottom: "2px solid #6a4c93", paddingBottom: "5px" }}>Statistics</h2>

                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "15px" }}>
                  <tbody>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", width: "50%", fontSize: "14px" }}>Total Cash</td>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontSize: "14px" }}>£{stats.totalCashAmount.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", fontSize: "14px" }}>Total POS Card</td>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontSize: "14px" }}>£{stats.totalPOSCardAmount.toFixed(2)}</td>
                    </tr>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", fontSize: "14px" }}>Total Stripe Card</td>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontSize: "14px" }}>£{stats.totalStripeAmount.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", fontSize: "14px" }}>Unpaid Orders</td>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontSize: "14px" }}>£{stats.unpaidAmount.toFixed(2)}</td>
                    </tr>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", fontSize: "14px" }}>Completed Orders</td>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontSize: "14px" }}>{stats.completedOrders}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", fontSize: "14px" }}>Pending Orders</td>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontSize: "14px" }}>{stats.pendingOrders}</td>
                    </tr>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", fontSize: "14px" }}>Cancelled Orders Count</td>
                      <td style={{ padding: "10px", border: "1px solid #ddd", fontSize: "14px" }}>{stats.cancelledCount}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 5. Order Source Breakdown */}
              {chartData && hasData(chartData.sales_by_order_source) && (
                <div style={{ marginBottom: "25px", pageBreakInside: "avoid" }}>
                  <h2 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "12px", color: "#6a4c93", borderBottom: "2px solid #6a4c93", paddingBottom: "5px" }}>Order Source Breakdown</h2>

                  <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "15px" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#6a4c93", color: "#fff" }}>
                        <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "left", fontSize: "14px" }}>Source</th>
                        <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "center", fontSize: "14px" }}>Orders</th>
                        <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "right", fontSize: "14px" }}>Total Sales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chartData.sales_by_order_source.map((item, idx) => (
                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#f5f5f5" : "#fff" }}>
                          <td style={{ padding: "10px", border: "1px solid #ddd", fontSize: "14px", fontWeight: "bold" }}>
                            {item.source}
                          </td>
                          <td style={{ padding: "10px", border: "1px solid #ddd", textAlign: "center", fontSize: "14px" }}>
                            {item.count}
                          </td>
                          <td style={{ padding: "10px", border: "1px solid #ddd", textAlign: "right", fontSize: "14px", fontWeight: "bold", color: "#2d5016" }}>
                            £{item.total.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 6. Order Type Breakdown */}
              {chartData && hasData(chartData.sales_by_order_type) && (
                <div style={{ marginBottom: "25px", pageBreakInside: "avoid" }}>
                  <h2 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "12px", color: "#6a4c93", borderBottom: "2px solid #6a4c93", paddingBottom: "5px" }}>Order Type Breakdown</h2>

                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#6a4c93", color: "#fff" }}>
                        <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "left", fontSize: "14px" }}>Order Type</th>
                        <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "center", fontSize: "14px" }}>Orders</th>
                        <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "right", fontSize: "14px" }}>Total Sales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chartData.sales_by_order_type.map((item, idx) => (
                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#f5f5f5" : "#fff" }}>
                          <td style={{ padding: "10px", border: "1px solid #ddd", fontSize: "14px", fontWeight: "bold" }}>
                            {item.order_type}
                          </td>
                          <td style={{ padding: "10px", border: "1px solid #ddd", textAlign: "center", fontSize: "14px" }}>
                            {item.count}
                          </td>
                          <td style={{ padding: "10px", border: "1px solid #ddd", textAlign: "right", fontSize: "14px", fontWeight: "bold", color: "#2d5016" }}>
                            £{item.total.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportSettings;